import {
  BackLink,
  cn,
  Icon,
  Kbd,
  PageHeaderAddon,
  PageParentProvider,
  SwipeBackIndicator,
  useIsDesktop,
} from '@nexus/ui';
import { Outlet, useRouterState } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GuideButton } from '../guides/guide-button';
import { buildNavigation, moduleForPath } from '../modules/navigation';
import { OfflineProvider } from '../offline/offline';
import { useModules } from '../modules/context';
import { useSession } from '../session/session';
import { AppUpdateNotice } from './app-update-notice';
import { BottomBar } from './bottom-bar';
import { CompanySwitcher } from './company-switcher';
import { MfaBanner } from './mfa-banner';
import { NavMenu } from './nav-menu';
import { NotificationsPanel } from './notifications-panel';
import { usePageBack } from './page-back';
import { ProfileMenu } from './profile-menu';
import { PullToRefresh } from './pull-to-refresh';
import { PushPrompt } from './push-prompt';
import { isApple, SearchPalette, useSearchShortcut } from './search-palette';
import { Sidebar } from './sidebar';
import { SWIPE_BACK_THRESHOLD, useEdgeSwipeBack } from './use-edge-swipe-back';
import { SyncPanel } from './sync-panel';

const SIDEBAR_KEY = 'nexus.sidebar';

function useSidebarState(): [boolean, () => void] {
  const desktop = useIsDesktop();
  const [stored, setStored] = useState<'open' | 'collapsed' | null>(() => {
    try {
      return localStorage.getItem(SIDEBAR_KEY) as 'open' | 'collapsed' | null;
    } catch {
      return null;
    }
  });
  // Tablets start with the narrow bar, desktops with the full one, until the person decides.
  const collapsed = stored ? stored === 'collapsed' : !desktop;
  const toggle = () => {
    const next = collapsed ? 'open' : 'collapsed';
    setStored(next);
    try {
      localStorage.setItem(SIDEBAR_KEY, next);
    } catch {
      // Without storage the choice lasts until reload.
    }
  };
  return [collapsed, toggle];
}

/** Frame of every page after login: top bar, navigation, banners and the content area. */
export function AppLayout() {
  return (
    <OfflineProvider>
      <AppFrame />
    </OfflineProvider>
  );
}

function AppFrame() {
  const { t } = useTranslation();
  const session = useSession();
  const modules = useModules();
  const navigation = useMemo(() => buildNavigation(modules, session), [modules, session]);
  const path = useRouterState({ select: (state) => state.location.pathname });
  const accent = moduleForPath(navigation, path)?.accent;
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [collapsed, toggleSidebar] = useSidebarState();
  const pageBack = usePageBack();
  const { parent } = pageBack;
  const swipe = useEdgeSwipeBack(Boolean(parent), pageBack.goBack);
  const openSearch = useCallback(() => setSearchOpen(true), []);
  useSearchShortcut(openSearch);

  return (
    <PageParentProvider value={pageBack}>
      <div className="min-h-dvh bg-canvas">
        <a
          href="#content"
          className="sr-only z-50 rounded-md bg-surface px-3 py-2 font-medium focus:not-sr-only focus:fixed focus:top-2 focus:left-2"
        >
          {t('shell.skipToContent')}
        </a>

        {/* Three columns from the tablet on: the search sits in the middle of the window, however
          wide company and icons are, and does not move when the sidebar collapses. */}
        <header className="sticky top-0 z-40 flex h-(--topbar-h) items-center gap-2 border-b border-line bg-surface px-3 md:grid md:grid-cols-[1fr_minmax(0,24rem)_1fr] md:px-4">
          {/* On the phone, pages below a list show the way back here instead of the company, as in
            iOS apps; it stays in view while scrolling. The switch stays in the menu. */}
          <div className="flex min-w-0 flex-1">
            {parent && <BackLink parent={parent} className="-ml-1 md:hidden" />}
            <div className={cn('flex min-w-0', parent && 'max-md:hidden')}>
              <CompanySwitcher />
            </div>
          </div>
          <button
            type="button"
            onClick={openSearch}
            className="hidden h-control w-full cursor-pointer items-center gap-2 rounded-md border border-line bg-canvas px-3 text-fg-subtle transition-colors transition-fast hover:border-line-strong hover:text-fg-muted md:flex"
          >
            <Icon name="search" />
            <span className="flex-1 text-left">{t('shell.search.open')}</span>
            <Kbd>{isApple() ? '⌘K' : t('shell.search.shortcut')}</Kbd>
          </button>
          <div className="flex items-center justify-end gap-0.5">
            <SyncPanel />
            <NotificationsPanel />
            <ProfileMenu />
          </div>
        </header>

        <AppUpdateNotice />
        <MfaBanner />
        <PushPrompt />

        <div className="flex">
          <Sidebar navigation={navigation} collapsed={collapsed} onToggle={toggleSidebar} />
          <main
            id="content"
            tabIndex={-1}
            data-module-accent={accent}
            className={cn(
              'min-w-0 flex-1 px-(--page-gutter) pt-(--page-gutter) outline-none',
              // Room for the bottom bar and the fixed primary action on the phone.
              'pb-[calc(var(--bottombar-h)+5rem)] md:pb-10',
            )}
          >
            <div className="mx-auto w-full max-w-(--page-max)">
              <PageHeaderAddon value={<GuideButton />}>
                <Outlet />
              </PageHeaderAddon>
            </div>
          </main>
        </div>

        <BottomBar
          menuOpen={menuOpen}
          onOpenMenu={() => setMenuOpen(true)}
          onOpenSearch={openSearch}
        />
        <PullToRefresh />
        <SwipeBackIndicator distance={swipe} threshold={SWIPE_BACK_THRESHOLD} />
        <NavMenu navigation={navigation} open={menuOpen} onOpenChange={setMenuOpen} />
        <SearchPalette open={searchOpen} onOpenChange={setSearchOpen} navigation={navigation} />
      </div>
    </PageParentProvider>
  );
}
